import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react'
import { useState, useEffect } from 'react'

export default function CardsVerticais({ initialSections, height = 'h-[210px]', color = '#130B57' }) {
  // Define os conteúdos com base nos inicialmente fornecidos
  const [sections, setSections] = useState(
    initialSections || [
      {
        title: 'Título Superior',
        content: 'Conteúdo do card superior.',
      },
      {
        title: 'Título Central',
        content: 'Conteúdo do card central em destaque.',
      },
      {
        title: 'Título Inferior',
        content: 'Conteúdo do card inferior.',
      },
    ],
  )

  // Acompanha o índice ativo atual (inicialmente 1, o do meio)
  const [activeIndex, setActiveIndex] = useState(1)
  
  // Estado para controlar a animação de fade
  const [isAnimating, setIsAnimating] = useState(false)
  const [visibleContent, setVisibleContent] = useState(sections[1])

  // Função para mover o conteúdo quando a div superior for clicada
  // Função para animar a transição de conteúdo
  const animateContentChange = (newSections, newActiveIndex, newMiddleContent) => {
    // Inicia a animação de fade out
    setIsAnimating(true);
    
    // Após um pequeno delay, atualiza o conteúdo e inicia o fade in
    setTimeout(() => {
      setSections(newSections);
      setActiveIndex(newActiveIndex);
      setVisibleContent(newMiddleContent);
      
      // Finaliza a animação após a transição
      setTimeout(() => {
        setIsAnimating(false);
      }, 300);
    }, 300);
  };
  
  const handleTopClick = () => {
    // Determinar qual seção está sendo movida para o meio
    const movingToMiddle = sections[0];
    // Encontrar o índice original desta seção
    const originalIndex = initialSections.findIndex(section => section.title === movingToMiddle.title);
    
    const newSections = [
      sections[1], // O conteúdo do meio vai para cima
      sections[0], // O conteúdo de cima vai para o meio
      sections[2], // O conteúdo de baixo permanece igual
    ];
    
    // Anima a transição
    animateContentChange(newSections, originalIndex, movingToMiddle);
  }

  // Função para mover o conteúdo quando a div inferior for clicada
  const handleBottomClick = () => {
    // Determinar qual seção está sendo movida para o meio
    const movingToMiddle = sections[2];
    // Encontrar o índice original desta seção
    const originalIndex = initialSections.findIndex(section => section.title === movingToMiddle.title);
    
    const newSections = [
      sections[0], // O conteúdo de cima permanece igual
      sections[2], // O conteúdo de baixo vai para o meio
      sections[1], // O conteúdo do meio vai para baixo
    ];
    
    // Anima a transição
    animateContentChange(newSections, originalIndex, movingToMiddle);
  }

  // Função para rotacionar para a esquerda
  const handleLeftClick = () => {
    // Determinar qual seção está indo para o meio
    const movingToMiddle = sections[2];
    // Encontrar o índice original desta seção
    const originalIndex = initialSections.findIndex(section => section.title === movingToMiddle.title);
    
    const newSections = [
      sections[1], // O meio vai para cima
      sections[2], // O último vai para o meio
      sections[0], // O primeiro vai para o final
    ];
    
    // Anima a transição
    animateContentChange(newSections, originalIndex, movingToMiddle);
  }

  // Função para rotacionar para a direita
  const handleRightClick = () => {
    // Determinar qual seção está indo para o meio
    const movingToMiddle = sections[0];
    // Encontrar o índice original desta seção
    const originalIndex = initialSections.findIndex(section => section.title === movingToMiddle.title);
    
    const newSections = [
      sections[2], // O último vai para cima
      sections[0], // O primeiro vai para o meio
      sections[1], // O meio vai para o final
    ];
    
    // Anima a transição
    animateContentChange(newSections, originalIndex, movingToMiddle);
  }

  // Atualiza o conteúdo visível quando as seções mudam
  useEffect(() => {
    if (!isAnimating) {
      setVisibleContent(sections[1]);
    }
  }, [sections, isAnimating]);
  
  // Função para lidar com o clique nos dots
  const handleDotClick = index => {
    if (index === activeIndex) return // Não faz nada se já estiver ativo

    // Encontrar a seção correspondente ao índice clicado nos initialSections
    const targetSection = initialSections[index];
    
    // Encontrar onde esta seção está atualmente no array sections
    const currentPosition = sections.findIndex(section => section.title === targetSection.title);
    
    let newSections = [...sections];
    
    // Reorganizar os cards com base na posição atual da seção alvo
    if (currentPosition === 0) { // Se a seção alvo está na posição superior
      newSections = [
        sections[1], // O meio vai para cima
        sections[0], // O superior vai para o meio
        sections[2], // O inferior permanece embaixo
      ];
      // Anima a transição
      animateContentChange(newSections, index, sections[0]);
    } else if (currentPosition === 1) { // Se a seção alvo já está no meio
      // Não precisa reorganizar, já está na posição correta
      setActiveIndex(index);
    } else if (currentPosition === 2) { // Se a seção alvo está na posição inferior
      newSections = [
        sections[0], // O superior permanece em cima
        sections[2], // O inferior vai para o meio
        sections[1], // O meio vai para baixo
      ];
      // Anima a transição
      animateContentChange(newSections, index, sections[2]);
    }
  }

  return (
    <>
      <div className="hidden md:block relative">
        <div className="flex">
          <div className="flex-grow">
            <div
              className="rounded-t-[10px] py-5 px-10 backdrop-blur-md bg-[#FFFFFF17] mx-8 cursor-pointer hover:bg-white/10 transition-colors duration-300"
              onClick={handleTopClick}
            >
              <p className={`text-[${color}] uppercase font-inter-medium`}>{sections[0].title}</p>
            </div>
            <div className={`ring ring-white rounded-[10px] py-14 px-10 backdrop-blur-md bg-[#FFFFFF17] space-y-4 relative z-10 shadow-md ${height} overflow-hidden flex items-center justify-center`}>
              <div className={`transition-opacity duration-300 ${isAnimating ? 'opacity-0' : 'opacity-100'}`}>
                <h3 className="text-lg font-inter-bold uppercase">
                  {visibleContent.title}:
                </h3>
                <p>{visibleContent.content}</p>
              </div>
            </div>
            <div
              className="rounded-b-[10px] py-5 px-10 backdrop-blur-md bg-[#FFFFFF17] mx-8 cursor-pointer hover:bg-white/10 transition-colors duration-300"
              onClick={handleBottomClick}
            >
              <p className={`text-[${color}] uppercase font-inter-medium`}>{sections[2].title}</p>
            </div>
          </div>
          
          {/* Desktop dots navigation */}
          <div className="flex flex-col justify-center items-center gap-3 ml-8 mr-2">
            {sections.map((_, i) => (
              <div
                key={i}
                onClick={() => handleDotClick(i)}
                className={`rounded-full cursor-pointer transition-all duration-300 ${
                  i === activeIndex
                    ? 'bg-transparent border border-white'
                    : 'bg-white'
                } ${
                  i === 0 ? 'h-[23px] w-[6px]' : 
                  i === 1 ? 'h-[15px] w-[6px]' : 
                  'h-[6px] w-[6px]'
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      <div className="md:hidden">
        <section className="flex">
          <div className={`ring z-20 ring-white rounded-[10px] max-w-72 py-16 px-10 backdrop-blur-md bg-[#FFFFFF17] space-y-4 relative shadow-md overflow-hidden ${height === 'h-[130px]' ? 'h-[280px]' : 'h-[320px]'} flex items-center justify-center`}>
            <div className={`transition-opacity duration-300 ${isAnimating ? 'opacity-0' : 'opacity-100'} w-full`}>
              <h3 className="text-lg font-inter-bold uppercase">
                {sections[0].title}:
              </h3>
              <p className='mt-2'>{sections[0].content}</p>
            </div>
          </div>
          <div
            className="flex items-center justify-center z-10 mx-auto relative writing-mode-vertical-rl rounded-tl-[10px] rounded-bl-[10px] rotate-180 py-3 backdrop-blur-md ring-1 ring-white bg-[#FFFFFF17] cursor-pointer hover:bg-white/10 transition-colors duration-300"
            style={{ writingMode: 'vertical-rl', height: '224px' }}
            onClick={handleTopClick}
          >
            <p className="text-[#130B57] uppercase text-xs font-inter-medium text-center">
              {sections[1].title}
            </p>
          </div>
          <div
            className="flex justify-center items-center h-full mx-auto relative writing-mode-vertical-rl rounded-tl-[10px] rounded-bl-[10px] rotate-180 py-3 backdrop-blur-md ring-1 ring-white bg-[#FFFFFF17] cursor-pointer hover:bg-white/10 transition-colors duration-300"
            style={{ writingMode: 'vertical-rl', height: '180px' }}
            onClick={handleTopClick}
          >
            <p className="text-[#130B57] uppercase text-xs font-inter-medium text-center">
              {sections[2].title}
            </p>
          </div>
        </section>

        <div className="mt-8 flex justify-center items-center gap-2.5">
          <button type="button" onClick={handleLeftClick}>
            <ChevronLeftIcon className="size-10" />
          </button>

          {/* Carousel indicators */}
          <div className="flex flex-row gap-3 items-center">
            {sections.map((_, i) => (
              <div
                key={i}
                onClick={() => handleDotClick(i)}
                className={`h-[6px] rounded-full cursor-pointer transition-all duration-300 ${
                  i === activeIndex
                    ? 'bg-transparent border border-white'
                    : 'bg-white'
                } ${i === 0 ? 'w-[23px]' : i === 1 ? 'w-[15px]' : 'w-[6px]'}`}
              />
            ))}
          </div>

          <button type="button" onClick={handleRightClick}>
            <ChevronRightIcon className="size-10" />
          </button>
        </div>
      </div>
    </>
  )
}
